import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './News.css';

const ANI_LIST_API_URL = 'https://graphql.anilist.co';
const KITSU_API_URL = 'https://kitsu.io/api/edge/anime';
const TMDB_API_URL = `https://api.themoviedb.org/3/discover/tv?api_key=YOUR_TMDB_API_KEY&language=en-US&with_genres=16`; // Replace YOUR_TMDB_API_KEY
const GHIBLI_API_URL = 'https://ghibliapi.vercel.app/films';

const News = () => {
  const [aniListArticles, setAniListArticles] = useState([]);
  const [kitsuArticles, setKitsuArticles] = useState([]);
  const [tmdbArticles, setTmdbArticles] = useState([]);
  const [ghibliArticles, setGhibliArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch AniList Data
  const fetchAniListData = async () => {
    try {
      const response = await fetch(ANI_LIST_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `
            query {
              Page(page: 1, perPage: 6) {
                media(type: ANIME) {
                  id
                  title {
                    english
                    native
                  }
                  description
                  coverImage {
                    medium
                  }
                  siteUrl
                }
              }
            }
          `
        })
      });
      const data = await response.json();
      return data?.data?.Page?.media.map(item => ({
        id: item.id,
        title: item.title.english || item.title.native,
        description: item.description,
        image: item.coverImage.medium,
        url: item.siteUrl
      })) || [];
    } catch (err) {
      console.error('AniList API Error:', err);
      return [];
    }
  };

  // Fetch Kitsu Data
  const fetchKitsuData = async () => {
    try {
      const response = await fetch(`${KITSU_API_URL}?page[limit]=6`);
      const data = await response.json();
      return data?.data.map(item => ({
        id: item.id,
        title: item.attributes.titles.en || item.attributes.titles.ja_jp,
        description: item.attributes.synopsis,
        image: item.attributes.posterImage.medium,
        url: `https://kitsu.io/anime/${item.id}`
      })) || [];
    } catch (err) {
      console.error('Kitsu API Error:', err);
      return [];
    }
  };

  // Fetch TMDb Data
  const fetchTmdbData = async () => {
    try {
      const response = await fetch(TMDB_API_URL);
      const data = await response.json();
      return data?.results.map(item => ({
        id: item.id,
        title: item.name,
        description: item.overview,
        image: `https://image.tmdb.org/t/p/w500${item.poster_path}`,
        url: `https://www.themoviedb.org/tv/${item.id}`
      })) || [];
    } catch (err) {
      console.error('TMDb API Error:', err);
      return [];
    }
  };

  // Fetch Studio Ghibli Data
  const fetchGhibliData = async () => {
    try {
      const response = await fetch(GHIBLI_API_URL);
      const data = await response.json();
      return data.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        image: item.image || 'https://via.placeholder.com/150',
        url: `https://ghibliapi.vercel.app/films/${item.id}`
      })) || [];
    } catch (err) {
      console.error('Studio Ghibli API Error:', err);
      return [];
    }
  };

  // Fetch and combine data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [aniList, kitsu, tmdb, ghibli] = await Promise.all([
          fetchAniListData(),
          fetchKitsuData(),
          fetchTmdbData(),
          fetchGhibliData()
        ]);
        setAniListArticles(aniList);
        setKitsuArticles(kitsu);
        setTmdbArticles(tmdb);
        setGhibliArticles(ghibli);
        setLoading(false);
      } catch (err) {
        console.error('Data Fetch Error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
<section className="news-section">
  {/* First Section: Studio Ghibli */}
  <div className="news-horizontal-section">
    <h2>Studio Ghibli Films</h2>
    <div className="news-horizontal-grid">
      {ghibliArticles.map(newsItem => (
        <div className="news-horizontal-item" key={newsItem.id}>
          <img className="news-horizontal-image" src={newsItem.image} alt={newsItem.title} />
          <div className="news-horizontal-content">
            <h3>{newsItem.title}</h3>
            <p>{newsItem.description}</p>
            <Link to={`/news/${encodeURIComponent(newsItem.url)}`} className="news-read-more">
              Read More
            </Link>
          </div>
        </div>
      ))}
    </div>
  </div>

  {/* Main Content: AniList */}
  <div className="news-main-wrapper">
    <div className="news-main-content">
      <h1 className="news-title">AniList Updates</h1>
      <p className="news-subtitle">
        Subscribe to learn about new product features, articles, and updates.
      </p>
      <div className="news-grid">
        {aniListArticles.map(newsItem => (
          <div className="news-grid-item" key={newsItem.id}>
            <img className="news-grid-image" src={newsItem.image} alt={newsItem.title} />
            <div className="news-grid-content">
              <h3>{newsItem.title}</h3>
              <p>{newsItem.description}</p>
              <Link to={`/news/${encodeURIComponent(newsItem.url)}`} className="news-read-more">
                Read More
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Right Sidebar: Kitsu */}
    <div className="news-right-sidebar">
      <h2>Kitsu Highlights</h2>
      {kitsuArticles.map(newsItem => (
        <div className="news-sidebar-item" key={newsItem.id}>
          <img className="news-sidebar-image" src={newsItem.image} alt={newsItem.title} />
          <div className="news-sidebar-content">
            <h3>{newsItem.title}</h3>
            <p>{newsItem.description}</p>
            <Link to={`/news/${encodeURIComponent(newsItem.url)}`} className="news-read-more">
              Read More
            </Link>
          </div>
        </div>
      ))}
    </div>
  </div>

  {/* Horizontal News Section: TMDb */}
  <div className="news-horizontal-section">
    <div className="news-horizontal-grid">
      <h2>TMDb Discoveries</h2>
      {tmdbArticles.map(newsItem => (
        <div className="news-horizontal-item" key={newsItem.id}>
          <img className="news-horizontal-image" src={newsItem.image} alt={newsItem.title} />
          <div className="news-horizontal-content">
            <h3>{newsItem.title}</h3>
            <p>{newsItem.description}</p>
            <Link to={`/news/${encodeURIComponent(newsItem.url)}`} className="news-read-more">
              Read More
            </Link>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

  );
};

export default News;
